import { useState } from "react";
import { toast } from "sonner";
import apiClient from "../services/api";
import { jobsService } from "../services/api";
export const usePurchaseOrders = ({ job, onRefreshJob, getAuthHeaders, confirm }) => {
  const [createPOOpen, setCreatePOOpen] = useState(false);
  const [editPOOpen, setEditPOOpen] = useState(false);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [expandedPO, setExpandedPO] = useState(null);
  const [saving, setSaving] = useState(false);

  const [poForm, setPOForm] = useState({
    supplier_id: "",
    ship_to_gc_id: "",
    ship_to_street: "",
    ship_to_city_state: "",
    delivery_date: "",
    shipping_method: "",
    notes: "",
    selected_materials: [],
    manual_items: []
  });

  const [deliveryItems, setDeliveryItems] = useState([]);

  const purchaseOrders = job?.purchase_orders || [];

  // Equipment invoice handlers
  const handleAddEquipmentInvoice = async (invoiceData) => {
    try {
      await jobsService.addEquipmentInvoice(job.id, invoiceData);
      toast.success("Equipment invoice added");
      onRefreshJob();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add invoice");
      return false;
    }
  };

  const handleEditEquipmentInvoice = async (invoiceId, invoiceData) => {
    try {
      await jobsService.updateEquipmentInvoice(job.id, invoiceId, invoiceData);
      toast.success("Invoice updated");
      onRefreshJob();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update invoice");
      return false;
    }
  };

  const handleDeleteEquipmentInvoice = async (invoiceId) => {
    try {
      await jobsService.deleteEquipmentInvoice(job.id, invoiceId);
      toast.success("Invoice deleted");
      onRefreshJob();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete invoice");
    }
  };

  const handleToggleEquipmentPaid = async (inv) => {
    try {
      await jobsService.updateEquipmentInvoice(job.id, inv.id, { paid: !inv.paid });
      onRefreshJob();
    } catch (err) {
      toast.error("Failed to update invoice");
    }
  };

  const getBidSheetMaterials = () => {
    const materials = [];
    const bidSheets = job?.bid_sheets || [];
    for (const sheet of bidSheets) {
      // Only include accepted bid sheets and change orders
      const isChangeOrder = sheet.bid_type === "change_order" || sheet.is_change_order;
      if (!isChangeOrder && !sheet.is_accepted) continue;
      
      const sheetLabel = sheet.name || "Base Bid";
      for (const item of (sheet.material_items || [])) {
        materials.push({
          id: `${sheet.id}_${item.id}`,
          item_id: item.id,
          sheet_name: sheetLabel,
          sheet_id: sheet.id,
          description: item.item_name || item.description || item.name || "",
          qty: item.quantity || 0,
          unit: item.unit || "EA",
          item: item.category || "",
          total: item.total || 0,
        });
      }
    }
    return materials;
  };

  const resetPOForm = () => {
    const street = [job?.street, job?.apt].filter(Boolean).join(", ");
    const cityState = [job?.city, job?.state].filter(Boolean).join(", ");
    const cityStateZip = [cityState, job?.zip_code].filter(Boolean).join(" ");

    setPOForm({
      supplier_id: "",
      ship_to_gc_id: job?.general_contractor_id || "",
      ship_to_street: street,
      ship_to_city_state: cityStateZip,
      delivery_date: "",
      shipping_method: "",
      notes: "",
      selected_materials: [],
      manual_items: []
    });
  };

  const normalize = (s) => (s || "").toString().trim().toLowerCase();

  // Qty already ordered per bid-sheet material across non-cancelled POs (excluding one PO when editing)
  const getOrderedQtyMap = (materials, excludePOId = null) => {
    const byId = {};
    const byDesc = {};
    const knownIds = new Set(materials.map(m => m.id));
    for (const po of purchaseOrders) {
      if (po.status === "cancelled" || po.id === excludePOId) continue;
      for (const li of (po.line_items || [])) {
        const qty = parseFloat(li.qty) || 0;
        if (li.bid_material_id && knownIds.has(li.bid_material_id)) {
          byId[li.bid_material_id] = (byId[li.bid_material_id] || 0) + qty;
        } else {
          const key = normalize(li.description);
          if (key) byDesc[key] = (byDesc[key] || 0) + qty;
        }
      }
    }
    const map = {};
    for (const mat of materials) {
      map[mat.id] = (byId[mat.id] || 0) + (byDesc[normalize(mat.description)] || 0);
    }
    return map;
  };

  const openCreatePO = () => {
    resetPOForm();
    const materials = getBidSheetMaterials();
    const ordered = getOrderedQtyMap(materials);
    const imported = materials.map(mat => {
      const already = ordered[mat.id] || 0;
      const remaining = Math.max(0, (parseFloat(mat.qty) || 0) - already);
      return {
        id: mat.id,
        bid_material_id: mat.id,
        qty: remaining,
        unit: mat.unit,
        item: mat.item,
        description: mat.description,
        sheet_name: mat.sheet_name,
        bid_qty: parseFloat(mat.qty) || 0,
        ordered_qty: already,
      };
    });
    setPOForm(prev => ({ ...prev, manual_items: imported }));
    setCreatePOOpen(true);
  };

  const openEditPO = (po) => {
    setSelectedPO(po);
    setPOForm({
      supplier_id: po.supplier_id || "",
      ship_to_gc_id: po.ship_to_gc_id || "",
      ship_to_street: po.ship_to_street || "",
      ship_to_city_state: po.ship_to_city_state || "",
      delivery_date: po.delivery_date || "",
      shipping_method: po.shipping_method || "",
      notes: po.notes || "",
      selected_materials: [],
      manual_items: po.line_items?.map(li => ({
        id: li.id,
        qty: li.qty,
        unit: li.unit,
        item: li.item,
        description: li.description,
        bid_material_id: li.bid_material_id || null
      })) || []
    });
    setEditPOOpen(true);
  };

  const openDeliveryDialog = (po) => {
    setSelectedPO(po);
    setDeliveryItems(
      (po.line_items || []).map(li => ({
        id: li.id,
        description: li.description,
        qty: li.qty,
        unit: li.unit,
        qty_delivered: li.qty_delivered || 0,
        delivery_notes: li.delivery_notes || ""
      }))
    );
    setDeliveryOpen(true);
  };

  const toggleMaterial = (mat) => {
    setPOForm(prev => {
      const exists = prev.selected_materials.find(m => m.id === mat.id);
      if (exists) {
        return { ...prev, selected_materials: prev.selected_materials.filter(m => m.id !== mat.id) };
      }
      return {
        ...prev,
        selected_materials: [...prev.selected_materials, {
          id: mat.id,
          qty: mat.qty,
          unit: mat.unit,
          item: mat.item,
          description: mat.description
        }]
      };
    });
  };

  const updateSelectedMaterial = (id, field, value) => {
    setPOForm(prev => ({
      ...prev,
      selected_materials: prev.selected_materials.map(m =>
        m.id === id ? { ...m, [field]: value } : m
      )
    }));
  };

  const addManualItem = () => {
    setPOForm(prev => ({
      ...prev,
      manual_items: [...prev.manual_items, { id: crypto.randomUUID(), qty: "", unit: "EA", item: "", description: "" }]
    }));
  };

  const updateManualItem = (id, field, value) => {
    setPOForm(prev => ({
      ...prev,
      manual_items: prev.manual_items.map(m =>
        m.id === id ? { ...m, [field]: value } : m
      )
    }));
  };

  const removeManualItem = (id) => {
    setPOForm(prev => ({
      ...prev,
      manual_items: prev.manual_items.filter(m => m.id !== id)
    }));
  };

  const handleSavePO = async (isEdit = false) => {
    if (!poForm.supplier_id) {
      toast.error("Please select a supplier");
      return;
    }

    const toLineItem = (m) => ({
      qty: parseFloat(m.qty) || 0,
      unit: m.unit,
      item: m.item,
      description: m.description,
      bid_material_id: m.bid_material_id || null
    });
    // Imported bid rows left at 0 qty (already fully ordered) are skipped
    const activeManual = poForm.manual_items.filter(m => m.description && !(m.bid_material_id && !isEdit && (parseFloat(m.qty) || 0) <= 0));

    const allItems = [
      ...poForm.selected_materials.map(toLineItem),
      ...activeManual.map(toLineItem)
    ];

    if (allItems.length === 0) {
      toast.error("Please add at least one item");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        supplier_id: poForm.supplier_id,
        ship_to_gc_id: poForm.ship_to_gc_id || null,
        ship_to_street: poForm.ship_to_street,
        ship_to_city_state: poForm.ship_to_city_state,
        delivery_date: poForm.delivery_date || null,
        shipping_method: poForm.shipping_method || null,
        line_items: isEdit
          ? activeManual.map(m => ({ id: m.id, ...toLineItem(m) }))
          : allItems,
        notes: poForm.notes || null
      };

      if (isEdit && selectedPO) {
        await apiClient.put(
          `/jobs/${job.id}/purchase-orders/${selectedPO.id}`,
          payload
        );
        toast.success("Purchase order updated");
        setEditPOOpen(false);
      } else {
        await apiClient.post(
          `/jobs/${job.id}/purchase-orders`,
          payload
        );
        toast.success("Purchase order created");
        setCreatePOOpen(false);
      }
      onRefreshJob();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save purchase order");
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePO = async (poId) => {
    const ok = await confirm({ title: "Delete Purchase Order", message: "Delete this purchase order?", confirmText: "Delete", confirmVariant: "destructive" });
    if (!ok) return;
    try {
      await apiClient.delete(
        `/jobs/${job.id}/purchase-orders/${poId}`
      );
      toast.success("Purchase order deleted");
      onRefreshJob();
    } catch (err) {
      toast.error("Failed to delete purchase order");
    }
  };

  const handleSaveDelivery = async () => {
    if (!selectedPO) return;
    setSaving(true);
    try {
      await apiClient.put(
        `/jobs/${job.id}/purchase-orders/${selectedPO.id}/delivery`,
        { line_items: deliveryItems.map(li => ({ id: li.id, qty_delivered: parseFloat(li.qty_delivered) || 0, delivery_notes: li.delivery_notes })) }
      );
      toast.success("Delivery updated");
      setDeliveryOpen(false);
      onRefreshJob();
    } catch (err) {
      toast.error("Failed to update delivery");
    } finally {
      setSaving(false);
    }
  };

  const handleExportExcel = async (po) => {
    try {
      const response = await apiClient.get(
        `/jobs/${job.id}/purchase-orders/${po.id}/excel`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `PO_${po.po_number}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to export purchase order");
    }
  };

  return {
    // State
    createPOOpen, editPOOpen, deliveryOpen, selectedPO, expandedPO, saving,
    poForm, deliveryItems, purchaseOrders,
    // Setters
    setCreatePOOpen, setEditPOOpen, setDeliveryOpen, setExpandedPO,
    setPOForm, setDeliveryItems,
    // Handlers
    openCreatePO, openEditPO, openDeliveryDialog,
    toggleMaterial, updateSelectedMaterial, addManualItem,
    updateManualItem, removeManualItem,
    handleSavePO, handleDeletePO, handleSaveDelivery, handleExportExcel,
    getBidSheetMaterials,
    // Equipment invoice handlers
    handleAddEquipmentInvoice, handleEditEquipmentInvoice,
    handleDeleteEquipmentInvoice, handleToggleEquipmentPaid,
  };
};
